import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FaUser } from "react-icons/fa6";
import editIcon from "@/assets/icons/edit.svg";
import {
  UserDetails,
  getUserDetails,
  updateUserDetails,
} from "../services/userProfileServices";
import { useLoading } from "@/contexts/LoadingContext";
import toastService from "@/services/toastService";

// Type definition for form data
interface FormData {
  fullName: string;
  phoneNumber: string;
  addressDetails: string;
}

export default function UserProfile() {
  const [isUpdated, setIsUpdated] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | undefined>(
    undefined
  );
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    phoneNumber: "",
    addressDetails: "",
  });

  const { setLoading } = useLoading();

  useEffect(() => {
    // Fetch user details from API
    const fetchUserDetails = async () => {
      setLoading(true);
      const response = await getUserDetails();
      if (response && response.statusCode === 200) {
        console.log("User Details:", response);
        setUserDetails(response.data.userDetails);
        setFormData({
          fullName: response.data.userDetails.fullName,
          phoneNumber: response.data.userDetails.phoneNumber,
          addressDetails: response.data.userDetails.addressDetails || "",
        });
      }
      setLoading(false);
    };
    fetchUserDetails();
  }, [isUpdated]);

  /* Handles input changes and updates form state */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* Handles form submission */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Process form submission
    console.log("Form submitted:", formData);
    try {
      setLoading(true);

      const sanitizedFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          if (value && value.trim() !== "") {
            acc[key as keyof FormData] = value.trim();
          }
          return acc;
        },
        {} as Partial<FormData>
      );

      console.log("Sanitized form data:", sanitizedFormData);

      const response = await updateUserDetails(sanitizedFormData);
      if (response && response.statusCode === 200) {
        setIsUpdated(!isUpdated);
        toastService.success("Details saved successfully");
      }
    } catch (error) {
      console.log("error while updating user details", error);
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = "block text-sm font-medium text-[#616161] mb-2";
  const inputStyle = "w-full rounded-lg";
  const fieldStyle =
    "bg-white rounded-lg border border-slate-200 py-[13px] px-3 text-sm font-normal";

  return (
    <div className="mx-auto p-7">
      {/* Header */}
      <h1 className="text-xl font-medium text-bmpGreen-500 mb-6">
        Personal Information
      </h1>

      {/* Form Card */}
      <Card className="bg-[#f6f8fa] p-7 border-none rounded-2xl">
        <div>
          {/* Avatar Section */}
          <div className="mb-8 flex items-center">
            <div className="relative">
              <Avatar className="h-28 w-28 bg-gray-200">
                <AvatarFallback className="bg-gray-200 text-gray-600">
                  <FaUser size={50} />
                </AvatarFallback>
              </Avatar>
              <div className="absolute bottom-0 right-0 rounded-full cursor-pointer">
                <img src={editIcon} alt="edit icon" />
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className={labelStyle}>
                Full Name
              </label>
              <Input
                id="fullName"
                name="fullName"
                placeholder="Enter Full Name"
                value={formData.fullName}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={labelStyle}>
                Email
              </label>
              <div className={fieldStyle}>{userDetails?.email}</div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className={labelStyle}>
                Phone Number
              </label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                placeholder="Enter Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className={labelStyle}>
                Company Name
              </label>
              <div className={fieldStyle}>{userDetails?.companyName}</div>
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className={labelStyle}>
                Country
              </label>
              <div className={fieldStyle}>{userDetails?.countryName}</div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="addressDetails" className={labelStyle}>
                Address
              </label>
              <Input
                id="addressDetails"
                name="addressDetails"
                placeholder="Enter Address"
                value={formData.addressDetails}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <Button
              onClick={handleSubmit}
              className="bg-bmpGreen-500 hover:bg-bmpGreen-600 text-white px-12 py-3 rounded-lg text-lg font-semibold"
            >
              Save Details
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
